import React from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button.jsx";
import BasicDialogCritical from "@/components/ui/BasicDialogCritical.jsx";
import Card from "@/components/ui/Card.jsx";
import Select from "@/components/ui/Select.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import PharmacyFolaSmartCard from "@/pages/pharmacy/PharmacyFolaSmartCard.jsx";
import {
  buildPharmacyMemberRecords,
  createPharmacyInviteSignInUrl,
  createPharmacyInviteBrief,
  getPharmacyRolePermissionCopy,
  PHARMACY_EXPAND_MORE_ICON,
  PHARMACY_MEMBER_FLASH_KEY,
  parsePharmacyStaffCsvFile,
  PHARMACY_MEMBER_ROLE_OPTIONS,
  listAccessiblePharmacyStaffInvites,
  mergePersistedPharmacyMembers,
  persistPharmacyStaffInvite,
  removePharmacyStaffMember,
  revokePersistedPharmacyStaffMember,
  savePharmacyProfile,
  sendPharmacyInviteEmail,
  upsertPharmacyStaffMember,
} from "@/services/pharmacyLaneService.js";

const SHEET_CLASS =
  "rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[var(--elev-1)]";

function pickFirstNonEmpty(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function buildPharmacyInviteIdentity(profile = {}) {
  return {
    pharmacyLogoUrl: pickFirstNonEmpty(
      profile?.pharmacyLogoUrl,
      profile?.logoUrl,
      profile?.logo,
      profile?.brandingLogoUrl,
      profile?.brandLogoUrl,
      profile?.avatarUrl,
      profile?.imageUrl,
    ),
    pharmacyAddressLine1: pickFirstNonEmpty(
      profile?.addressLine1,
      profile?.address,
      profile?.streetAddress,
      profile?.street_address,
      profile?.line1,
    ),
    pharmacyAddressLine2: pickFirstNonEmpty(
      profile?.addressLine2,
      profile?.address2,
      profile?.streetAddress2,
      profile?.street_address_2,
      profile?.line2,
    ),
    pharmacyCityRegionPostal: [
      pickFirstNonEmpty(profile?.city),
      pickFirstNonEmpty(profile?.region, profile?.state, profile?.province),
      pickFirstNonEmpty(profile?.postalCode, profile?.postal_code, profile?.postcode, profile?.zip),
    ]
      .filter(Boolean)
      .join(", ")
      .replace(/,\s*,/g, ", ")
      .trim(),
    pharmacyCountry: pickFirstNonEmpty(profile?.country),
  };
}

function MemberStatusChip({ status = "active", t }) {
  const copy =
    status === "invited"
      ? t("pharmacy.settings.members.status.invited", "Invite • Pending")
      : t("pharmacy.settings.members.status.active", "Active");

  const toneClass =
    status === "invited"
      ? "bg-[rgba(var(--afrimed-primary),0.12)] text-[rgb(var(--afrimed-primary))]"
      : "bg-[rgba(0,189,196,0.12)] text-[#008A8F]";

  return (
    <span className={`inline-flex rounded-[12px] px-2.5 py-1 text-label-small ${toneClass}`}>
      {copy}
    </span>
  );
}

export default function PharmacySettingsMembersPanel({ profile, t }) {
  const navigate = useNavigate();
  const auth = useAuth() || {};
  const ownerUserId = String(auth?.user?.id || "").trim();
  const safeProfile = profile && typeof profile === "object" ? profile : {};
  const inputRef = React.useRef(null);
  const [copyState, setCopyState] = React.useState("");
  const [importState, setImportState] = React.useState("");
  const [actionState, setActionState] = React.useState({
    key: "",
    status: "",
  });
  const [memberPendingRemoval, setMemberPendingRemoval] = React.useState(null);

  const members = React.useMemo(() => buildPharmacyMemberRecords(safeProfile), [safeProfile]);
  const ownerCount = Math.max(
    1,
    members.filter((member) => member.roleValue === "owner").length,
  );
  const activeMembers = members.filter(
    (member) => member.roleValue !== "owner" && member.status === "active",
  );
  const invitedMembers = members.filter(
    (member) => member.roleValue !== "owner" && member.status === "invited",
  );
  const permissionSummaries = React.useMemo(
    () =>
      PHARMACY_MEMBER_ROLE_OPTIONS.map((option) => getPharmacyRolePermissionCopy(option.value)),
    [],
  );
  React.useEffect(() => {
    if (!copyState) return undefined;
    const timer = window.setTimeout(() => setCopyState(""), 7000);
    return () => window.clearTimeout(timer);
  }, [copyState]);

  React.useEffect(() => {
    if (!importState) return undefined;
    const timer = window.setTimeout(() => setImportState(""), 3000);
    return () => window.clearTimeout(timer);
  }, [importState]);

  React.useEffect(() => {
    try {
      const flash = String(sessionStorage.getItem(PHARMACY_MEMBER_FLASH_KEY) || "").trim();
      if (!flash) return undefined;
      sessionStorage.removeItem(PHARMACY_MEMBER_FLASH_KEY);
      setCopyState(flash);
    } catch {
      return undefined;
    }
    return undefined;
  }, []);

  const hydratePersistedMembers = React.useCallback(async () => {
    if (!ownerUserId) return;
    try {
      const persistedRows = await listAccessiblePharmacyStaffInvites();
      if (!Array.isArray(persistedRows) || !persistedRows.length) return;
      const nextProfile = mergePersistedPharmacyMembers(safeProfile, persistedRows);
      const currentSnapshot = JSON.stringify(buildPharmacyMemberRecords(safeProfile));
      const nextSnapshot = JSON.stringify(buildPharmacyMemberRecords(nextProfile));
      if (currentSnapshot !== nextSnapshot) {
        saveNextProfile(nextProfile);
      }
    } catch (error) {
      console.warn(
        "[PharmacySettingsMembersPanel] persisted member hydrate skipped:",
        error?.message || error,
      );
    }
  }, [ownerUserId, safeProfile]);

  React.useEffect(() => {
    void hydratePersistedMembers();
  }, [hydratePersistedMembers]);

  React.useEffect(() => {
    if (!ownerUserId) return undefined;

    function handleFocus() {
      void hydratePersistedMembers();
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        void hydratePersistedMembers();
      }
    }

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [hydratePersistedMembers, ownerUserId]);

  function saveNextProfile(nextProfile) {
    savePharmacyProfile(nextProfile);
  }

  async function handleCopyBrief() {
    try {
      await navigator.clipboard.writeText(createPharmacyInviteBrief(safeProfile));
      setCopyState(t("pharmacy.settings.members.copied", "Invite brief copied"));
    } catch {
      setCopyState(t("pharmacy.settings.members.copyFailed", "Could not copy right now"));
    }
  }

  async function handleImportFiles(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const parsed = await parsePharmacyStaffCsvFile(file);
      const mergedProfile = parsed.reduce(
        (current, member) => upsertPharmacyStaffMember(current, member),
        {
          ...safeProfile,
          staffCsvNames: [file.name],
        },
      );
      saveNextProfile(mergedProfile);
      setImportState(
        parsed.length
          ? t(
              "pharmacy.settings.members.imported",
              "Imported {count} staff records from {file}.",
              { count: parsed.length, file: file.name },
            )
          : t(
              "pharmacy.settings.members.importEmpty",
              "Uploaded {file}, but no usable staff rows were detected.",
              { file: file.name },
            ),
      );
    } catch {
      setImportState(
        t("pharmacy.settings.members.importFailed", "The staff CSV could not be parsed."),
      );
    } finally {
      if (event.target) event.target.value = "";
    }
  }

  function handleRoleChange(member, roleValue) {
    if (member.roleValue === "owner") return;
    saveNextProfile(
      upsertPharmacyStaffMember(safeProfile, {
        ...member,
        roleValue,
      }),
    );
  }

  async function handleRemove(member) {
    if (member.roleValue === "owner") return;
    setMemberPendingRemoval(member);
  }

  async function confirmRemoveMember() {
    const member = memberPendingRemoval;
    if (!member || member.roleValue === "owner") {
      setMemberPendingRemoval(null);
      return;
    }
    setActionState({ key: `${member.id}:remove`, status: "busy" });
    try {
      await revokePersistedPharmacyStaffMember({
        memberId: member.id,
        ownerUserId,
      });
      saveNextProfile(removePharmacyStaffMember(safeProfile, member.id));
      setActionState({ key: `${member.id}:remove`, status: "success" });
      setCopyState(t("pharmacy.settings.members.removed", "Member removed"));
    } catch (error) {
      setActionState({ key: `${member.id}:remove`, status: "error" });
      setCopyState(
        error?.message
          ? t(
              "pharmacy.settings.members.removeFailedDetail",
              "Remove member failed: {message}",
              { message: error.message },
            )
          : t("pharmacy.settings.members.removeFailed", "Could not remove this member right now."),
      );
    } finally {
      setMemberPendingRemoval(null);
    }
  }

  async function handleCopyInviteLink(member) {
    setActionState({ key: `${member.id}:copy`, status: "busy" });
    try {
      const persistedMember = await persistPharmacyStaffInvite({
        profile: safeProfile,
        member,
        ownerUserId,
      });
      const nextProfile = upsertPharmacyStaffMember(safeProfile, {
        ...member,
        ...persistedMember,
      });
      saveNextProfile(nextProfile);
      const url =
        persistedMember?.inviteLink ||
        member.inviteLink ||
        createPharmacyInviteSignInUrl(member.email);
      await navigator.clipboard.writeText(url);
      setActionState({ key: `${member.id}:copy`, status: "success" });
      setCopyState(t("pharmacy.settings.members.inviteLinkCopied", "Sign-in link copied"));
    } catch {
      try {
        await navigator.clipboard.writeText(member.inviteLink || createPharmacyInviteSignInUrl(member.email));
        setActionState({ key: `${member.id}:copy`, status: "success" });
        setCopyState(t("pharmacy.settings.members.inviteLinkCopied", "Sign-in link copied"));
      } catch {
        setActionState({ key: `${member.id}:copy`, status: "error" });
        setCopyState(t("pharmacy.settings.members.inviteLinkCopyFailed", "Could not copy sign-in link right now"));
      }
    }
  }

  async function handleResendInviteEmail(member) {
    setActionState({ key: `${member.id}:resend`, status: "busy" });
    setCopyState(t("pharmacy.settings.members.inviteSending", "Sending invitation email…"));
    try {
      const resolvedMember = {
        ...member,
        inviteLink:
          member?.inviteLink || createPharmacyInviteSignInUrl(member?.email, "/pharmacy/dashboard"),
      };

      await sendPharmacyInviteEmail({
        email: resolvedMember.email,
        fullName: resolvedMember.fullName,
        roleValue: resolvedMember.roleValue,
        pharmacyName: safeProfile?.pharmacyName || safeProfile?.tradingName || "",
        inviterName: safeProfile?.adminName || "",
        inviteLink: resolvedMember.inviteLink,
        ...buildPharmacyInviteIdentity(safeProfile),
      });

      let finalMember = resolvedMember;
      try {
        const persistedMember = await persistPharmacyStaffInvite({
          profile: safeProfile,
          member: resolvedMember,
          ownerUserId,
        });
        finalMember = {
          ...resolvedMember,
          ...persistedMember,
        };
      } catch (persistError) {
        console.warn(
          "[PharmacySettingsMembersPanel] invite persistence fallback:",
          persistError?.message || persistError,
        );
      }

      const nextProfile = upsertPharmacyStaffMember(safeProfile, finalMember);
      saveNextProfile(nextProfile);
      setActionState({ key: `${member.id}:resend`, status: "success" });
      setCopyState(t("pharmacy.settings.members.inviteEmailSent", "Invitation email sent"));
    } catch (error) {
      setActionState({ key: `${member.id}:resend`, status: "error" });
      setCopyState(
        error?.message
          ? t(
              "pharmacy.settings.members.inviteFailedDetail",
              "Invite email failed: {message}",
              { message: error.message },
            )
          : t(
              "pharmacy.settings.members.inviteEmailFailed",
              "Invite email could not be delivered right now. Use Copy sign-in link instead.",
            ),
      );
    }
  }

  return (
    <div className="space-y-7">
      <div>
        <h2 className="text-title-large text-[rgb(var(--clr-on-surface))]">
          {t("pharmacy.settings.members.title", "Members")}
        </h2>
        <p className="mt-1 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
          {t(
            "pharmacy.settings.members.subtitle",
            "Principal owner onboarding is already done. Add the branch team here and keep roles, payouts, delivery, and inventory handoff aligned.",
          )}
        </p>
      </div>

      <PharmacyFolaSmartCard
        title="Invite and assign branch roles"
        body="Add branch members, import staff by CSV, and assign scoped roles such as dispensing pharmacist, inventory manager, finance lead, delivery lead, or branch admin while owner privileges remain protected."
        cta="Ask Fola about roles"
        route="/pharmacy/messages"
        promptKey="team_roles"
        returnTo="/pharmacy/settings"
      />

      <div className="grid gap-5 lg:grid-cols-3">
        <section className={`${SHEET_CLASS} p-5`}>
          <div className="text-label-large text-[rgb(var(--clr-on-surface-variant))]">
            {t("pharmacy.settings.members.kpi.branchOwner", "Owner seats")}
          </div>
          <div className="mt-2 text-title-large text-[rgb(var(--clr-on-surface))]">{ownerCount}</div>
          <p className="mt-2 text-body-small text-[rgb(var(--clr-on-surface-variant))]">
            {t("pharmacy.settings.members.kpi.branchOwnerCopy", "The principal owner/admin remains the permission anchor for this branch.")}
          </p>
        </section>
        <section className={`${SHEET_CLASS} p-5`}>
          <div className="text-label-large text-[rgb(var(--clr-on-surface-variant))]">
            {t("pharmacy.settings.members.kpi.activeMembers", "Active members")}
          </div>
          <div className="mt-2 text-title-large text-[rgb(var(--clr-on-surface))]">{activeMembers.length}</div>
          <p className="mt-2 text-body-small text-[rgb(var(--clr-on-surface-variant))]">
            {t("pharmacy.settings.members.kpi.activeMembersCopy", "Only accepted members count as active. Invited rows stay pending until the person signs in officially.")}
          </p>
        </section>
        <section className={`${SHEET_CLASS} p-5`}>
          <div className="text-label-large text-[rgb(var(--clr-on-surface-variant))]">
            {t("pharmacy.settings.members.kpi.pendingInvites", "Pending invites")}
          </div>
          <div className="mt-2 text-title-large text-[rgb(var(--clr-on-surface))]">{invitedMembers.length}</div>
          <p className="mt-2 text-body-small text-[rgb(var(--clr-on-surface-variant))]">
            {t("pharmacy.settings.members.kpi.pendingInvitesCopy", "Use the invited email on the AfriMed login page. The row flips only after the invite is accepted and linked.")}
          </p>
        </section>
      </div>

      <section className="space-y-5">
        <div className="space-y-4">
          <div className="max-w-[700px]">
            <div className="text-title-medium text-[rgb(var(--clr-on-surface))]">
              {t("pharmacy.settings.members.inviteTitle", "Invite or import branch members")}
            </div>
            <p className="mt-1 text-body-small text-[rgb(var(--clr-on-surface-variant))]">
              {t(
                "pharmacy.settings.members.inviteCopy",
                "Copy a setup brief for payroll or operations teams, or import a staff CSV when the branch already manages staff in HR software.",
              )}
            </p>
            <p className="mt-2 text-body-small text-[rgb(var(--clr-on-surface-variant))]">
              {t(
                "pharmacy.settings.members.inviteFlowCopy",
                "New members stay Invited until they sign in with the invited email. Only then should the branch treat them as active.",
              )}
            </p>
            <p className="mt-2 text-body-small text-[rgb(var(--clr-on-surface-variant))]">
              {t(
                "pharmacy.settings.members.ownerCopy",
                "Owners can invite or remove staff and assign roles. Staff roles can help with branch fulfilment work, but they cannot change ownership, remove the pharmacy, or manage payment settings.",
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="text" onClick={handleCopyBrief}>
              {t("pharmacy.settings.members.copyBrief", "Copy invite brief")}
            </Button>
            <Button variant="tonal" onClick={() => inputRef.current?.click()}>
              {t("pharmacy.settings.members.importCsv", "Import CSV")}
            </Button>
            <Button onClick={() => navigate("/pharmacy/settings/members/new")}>
              {t("pharmacy.settings.members.addCta", "New member")}
            </Button>
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(event) => void handleImportFiles(event)}
        />

        <div className="space-y-5">
          <div className="mb-5 grid gap-3 lg:grid-cols-2">
            {permissionSummaries.map((item) => (
              <Card
                key={item.roleValue}
                className="p-4"
              >
                <div className="text-label-large text-[rgb(var(--clr-on-surface))]">
                  {item.label}
                </div>
                <p className="mt-1 text-body-small text-[rgb(var(--clr-on-surface-variant))]">
                  {item.detail}
                </p>
              </Card>
            ))}
          </div>

          {copyState || importState ? (
            <div className="mb-4 flex justify-end">
              <div className="w-full rounded-[12px] bg-[rgba(24,50,119,0.08)] px-4 py-3 text-body-small text-[rgb(var(--clr-on-surface-variant))] md:w-[320px]">
                {copyState || importState}
              </div>
            </div>
          ) : null}

          <div className="hidden border-b border-[rgba(24,50,119,0.12)] pb-2 text-body-medium text-[rgb(var(--clr-on-surface-variant))] md:grid md:grid-cols-12 md:gap-4">
            <div className="md:col-span-6">{t("pharmacy.settings.members.columns.name", "Name")}</div>
            <div className="md:col-span-4">{t("pharmacy.settings.members.columns.role", "Role")}</div>
            <div className="md:col-span-2">{t("pharmacy.settings.members.columns.actions", "Actions")}</div>
          </div>

          <div>
            {members.map((member, index) => (
              <div
                key={member.id}
                className={[
                  "grid gap-3 py-4 md:grid-cols-12 md:items-start md:gap-4",
                  index === 0 ? "" : "border-t border-[rgba(24,50,119,0.12)]",
                ].join(" ")}
              >
                <div className="min-w-0 md:col-span-6">
                  <div className="truncate text-body-large text-[rgb(var(--clr-on-surface))]">
                    {member.fullName || t("pharmacy.common.notSet", "Not set")}
                  </div>
                  <div className="mt-1 text-body-small text-[rgb(var(--clr-on-surface-variant))]">
                    {[member.email, member.phone].filter(Boolean).join(" • ") || "—"}
                  </div>
                  <div className="mt-1 text-body-small text-[rgb(var(--clr-on-surface-variant))]">
                    {member.branch || safeProfile.pharmacyName || "—"}
                  </div>
                  {member.roleValue !== "owner" ? (
                    <div className="mt-2 flex items-center gap-2">
                      <MemberStatusChip status={member.status} t={t} />
                      {member.status === "invited" ? (
                        <span className="text-body-small text-[rgb(var(--clr-on-surface-variant))]">
                          {t("pharmacy.settings.members.pendingHint", "Waiting for invitation acceptance")}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="relative z-20 md:col-span-4">
                  {member.roleValue === "owner" ? (
                    <div className="inline-flex min-h-8 items-center rounded-[12px] bg-[var(--state-primary-12)] px-4 text-label-large text-[rgb(var(--afrimed-primary))]">
                      {member.role}
                    </div>
                  ) : (
                    <div>
                      <Select
                        value={member.roleValue}
                        onValueChange={(nextValue) => handleRoleChange(member, nextValue)}
                        options={PHARMACY_MEMBER_ROLE_OPTIONS.filter((option) => option.value !== "owner")}
                        indicatorSrc={PHARMACY_EXPAND_MORE_ICON}
                        triggerClassName="min-h-10 rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] text-body-medium text-[rgb(var(--clr-on-surface))]"
                        valueClassName="truncate"
                        contentClassName="min-w-full rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.10),0px_6px_12px_-4px_rgba(26,27,35,0.08)]"
                        optionClassName="py-3 text-body-medium text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.04)]"
                      />
                      <p className="mt-2 text-body-small text-[rgb(var(--clr-on-surface-variant))]">
                        {getPharmacyRolePermissionCopy(member.roleValue).detail}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-center justify-start gap-2 md:col-span-2">
                  {member.roleValue === "owner" ? (
                    <div className="flex h-10 w-10 items-center justify-center text-body-small text-[rgb(var(--clr-on-surface-variant))]">
                      •
                    </div>
                  ) : (
                    <>
                      {member.status === "invited" ? (
                        <Button
                          variant="text"
                          size="sm"
                          className="rounded-[12px]"
                          disabled={actionState.key === `${member.id}:resend` && actionState.status === "busy"}
                          onClick={() => void handleResendInviteEmail(member)}
                        >
                          {actionState.key === `${member.id}:resend` && actionState.status === "busy"
                            ? t("pharmacy.settings.members.resendInviteBusy", "Sending…")
                            : actionState.key === `${member.id}:resend` && actionState.status === "success"
                              ? t("pharmacy.settings.members.resendInviteSent", "Sent")
                              : actionState.key === `${member.id}:resend` && actionState.status === "error"
                                ? t("pharmacy.settings.members.resendInviteFailed", "Retry invite")
                                : t("pharmacy.settings.members.resendInvite", "Resend invite")}
                        </Button>
                      ) : null}
                      {member.status === "invited" ? (
                        <Button
                          variant="text"
                          size="sm"
                          className="rounded-[12px]"
                          disabled={actionState.key === `${member.id}:copy` && actionState.status === "busy"}
                          onClick={() => void handleCopyInviteLink(member)}
                        >
                          {actionState.key === `${member.id}:copy` && actionState.status === "busy"
                            ? t("pharmacy.settings.members.copySignInLinkBusy", "Copying…")
                            : actionState.key === `${member.id}:copy` && actionState.status === "success"
                              ? t("pharmacy.settings.members.copySignInLinkDone", "Copied")
                              : actionState.key === `${member.id}:copy` && actionState.status === "error"
                                ? t("pharmacy.settings.members.copySignInLinkRetry", "Copy again")
                                : t("pharmacy.settings.members.copySignInLink", "Copy sign-in link")}
                        </Button>
                      ) : null}
                      <Button
                        variant="criticalTonal"
                        size="sm"
                        className="rounded-[12px]"
                        onClick={() => handleRemove(member)}
                        aria-label={t("pharmacy.settings.members.remove", "Remove member")}
                      >
                        {t("pharmacy.settings.members.removeLabel", "Remove")}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BasicDialogCritical
        open={!!memberPendingRemoval}
        title={t("pharmacy.settings.members.removeDialog.title", "Notice")}
        body={t(
          "pharmacy.settings.members.removeDialog.body",
          "Remove {member} from the branch team?",
          {
            member:
              memberPendingRemoval?.fullName ||
              memberPendingRemoval?.email ||
              t("pharmacy.settings.members.removeDialog.memberFallback", "this member"),
          },
        )}
        cancelText={t("pharmacy.settings.members.removeDialog.cancel", "Cancel")}
        okText={t("pharmacy.settings.members.removeDialog.confirm", "Yes")}
        cancelStyle={{
          background: "var(--state-primary-12)",
          color: "rgb(var(--afrimed-primary))",
        }}
        okStyle={{
          background: "var(--state-critical-12)",
          color: "rgb(var(--schemes-critical))",
        }}
        onCancel={() => setMemberPendingRemoval(null)}
        onOk={() => void confirmRemoveMember()}
      />
    </div>
  );
}
